import { WebsocketProvider } from 'y-websocket'
import * as Y from 'yjs'
import * as encoding from 'lib0/encoding'
import * as decoding from 'lib0/decoding'
import { IndexeddbPersistence } from 'y-indexeddb'
import * as syncProtocol from 'y-protocols/sync'
import * as awarenessProtocol from 'y-protocols/awareness'
import { CustomDoc } from './CustomDoc'

const messageMemberId = 42
const messageHardReset = 44
const messagePing = 45
const messagePong = 46
const messageDamaged = 48
const messageDataReceived = 49
const messageRepairing = 50
const messageConsistencyCheck = 51
const messageConsistencyCheckOK = 52
const messageConsistencyCheckPatch = 53 // selective patch: server-newer key-value pairs
const messageSync = 0
const messageAwareness = 1
const messageEvent = 47

// Helper function to send messages
const send = (doc: Y.Doc, provider: any, message: Uint8Array) => {
  if (provider.ws && provider.ws.readyState === WebSocket.OPEN) {
    provider.ws.send(message)
  }
}

interface Struct {
  type: number
  id?: number
  parentID?: number
  left?: number
  right?: number
  content?: string | any // Adjust based on actual content types
}

class CustomWebsocketProvider extends WebsocketProvider {
  // Re-declare inherited properties from WebsocketProvider that TypeScript
  // cannot resolve under bundler moduleResolution
  declare awareness: awarenessProtocol.Awareness
  declare wsconnected: boolean
  declare wsconnecting: boolean
  declare bcconnected: boolean
  declare wsUnsuccessfulReconnects: number
  declare messageHandlers: ((arg0: any, arg1: any, arg2: any, arg3: boolean, arg4: number) => void)[]
  declare _synced: boolean
  declare synced: boolean
  declare ws: WebSocket | null
  declare wsLastMessageReceived: number
  declare shouldConnect: boolean
  declare _resyncInterval: number
  declare url: string
  declare maxBackoffTime: number
  declare disableBc: boolean
  declare bcChannel: string
  declare _bcSubscriber: (data: ArrayBuffer, origin: any) => void
  declare _updateHandler: (update: Uint8Array, origin: any) => void
  declare _awarenessUpdateHandler: (changes: any, origin: any) => void
  declare _unloadHandler: () => void
  declare _checkInterval: any
  declare on: (event: string, handler: (...args: any[]) => void) => void
  declare off: (event: string, handler: (...args: any[]) => void) => void
  declare emit: (event: string, args: any[]) => void
  declare disconnect: () => void
  declare connectBc: () => void
  declare once: (event: string, handler: (...args: any[]) => void) => void
  memberId: string
  private memberIdSent: boolean = false
  private autoConnect: boolean
  private origAwareness: Function
  private origSync: Function
  shouldReconnect: boolean = true
  roomname: string
  doc: CustomDoc
  private isReconnecting: boolean = false
  private connectionCheckTimeout: NodeJS.Timeout | null = null
  private updateTrackingEnabled: boolean = true
  private pendingUpdates: Map<
    string,
    { timestamp: number; size: number; origin: any }
  > = new Map()
  private lastSyncAttempt: number = 0
  private lastWarningTime: number = 0
  private lastSyncComplete: number = 0
  private readonly SYNC_THROTTLE_MS = 10000 // Only attempt sync every 10 seconds
  private readonly WARNING_THROTTLE_MS = 30000 // Only log warnings every 30 seconds (reduced spam)
  private readonly SYNC_COMPLETE_GRACE_MS = 2000 // Don't warn for 2 seconds after sync completes
  _persistence: IndexeddbPersistence | null = null

  // Health monitoring for detecting zombie connections
  private lastMessageTime: number = Date.now()
  private lastConnectTime: number = 0
  private healthCheckInterval: NodeJS.Timeout | null = null

  // Timestamp of the last messageDataReceived (49) ack from the server.
  // Non-zero means y-websocket confirmed receipt of at least one data-map change.
  public lastDataAckTime: number = 0

  // When true, compact (clear) IDB on the next sync step 2 completion.
  // Set when messageRepairing is received — mirrors the Redis compaction we do in file-loader.
  private _compactIdbAfterSync: boolean = false

  // Pre-sync consistency gate.
  // When the server's SYNC_STEP_1 arrives we hold it here until IDB has loaded
  // and the consistency check has resolved. This prevents stale IDB data from
  // poisoning the server's Y.Doc via CRDT merge before we can detect and fix it.
  private _pendingServerSV: Uint8Array | null = null
  private _syncGateActive: boolean = false
  private _idbSynced: boolean = false
  private _syncGateTimeout: ReturnType<typeof setTimeout> | null = null
  private _hasTriggeredSelfRepair: boolean = false

  // Keys modified locally after consistency check was sent.
  // These must not be overwritten by the consistency check patch.
  private _dirtyKeysSinceCheck: Set<string> | null = null
  private _dirtyKeysObserver: ((event: Y.YMapEvent<string>) => void) | null = null

  // Create evaluation wait loop: cycles reconnect until Y.Map is populated by ydoc-service
  private _createEvalWaitTimer: ReturnType<typeof setInterval> | null = null

  // Ping/pong connection verification
  private pingInFlight: boolean = false
  private pendingPingTimeout: NodeJS.Timeout | null = null
  private readonly MESSAGE_TIMEOUT_MS = 30000 // No messages (including pings) in 30s = dead connection
  private readonly SYNC_VERIFICATION_DELAY_MS = 2000 // Verify sync completes within 2s of connect
  private readonly SYNC_FAILURE_TIMEOUT_MS = 5000 // If not synced after 5s total, force reconnect

  constructor(
    serverUrl: string,
    roomname: string,
    doc: CustomDoc,
    memberId: string,
    options: any = {}
  ) {
    // Validate room name consistency - ensure we're comparing room names, not memberIds
    if (doc.roomName !== roomname) {
      console.error('Room name mismatch:', {
        docRoom: doc.roomName,
        providerRoom: roomname,
        memberId: memberId,
      })
      throw new Error('Room name mismatch between doc and provider')
    }

    // Set up options with memberId in params
    options['params'] = { memberId }
    super(serverUrl + '/', roomname, doc, options)

    // Store memberId and roomname
    this.memberId = memberId
    this.roomname = roomname
    this.doc = doc
    this.shouldReconnect = true

    console.log('[CustomWebsocketProvider] Initialized', {
      roomname,
      memberId
    })

    // Override the parent's _updateHandler to suppress large pre-sync state dumps.
    // When IndexedDB loads and applies a full Y.Doc state (can be 50MB+), the parent
    // would forward that entire blob to the server before sync completes. This overwhelms
    // the server connection and causes 1006 abnormal close. The sync protocol (step 1 →
    // step 2) already handles bidirectional reconciliation without needing the bulk dump.
    const parentUpdateHandler = (this as any)._updateHandler
    if (parentUpdateHandler) {
      this.doc.off('update', parentUpdateHandler)
      const LARGE_UPDATE_THRESHOLD = 5 * 1024 * 1024 // 5MB
      const wrappedUpdateHandler = (update: Uint8Array, origin: any) => {
        if (!this.synced && update.length > LARGE_UPDATE_THRESHOLD) {
          console.debug('[UpdateHandler] Suppressing large pre-sync update — sync step 2 will reconcile', {
            updateSize: update.length,
            roomName: roomname,
          })
          return
        }
        // Diagnostic: log which updates are being forwarded to the server
        const isFromProvider = origin === this
        const isFromIDB = origin instanceof IndexeddbPersistence
        const originName = isFromProvider ? 'provider' : isFromIDB ? 'idb' : typeof origin === 'string' ? origin : 'unknown'
        if (!isFromProvider) {
          console.log(`[UpdateHandler] Forwarding update to server: origin=${originName}, size=${update.length}, synced=${this.synced}`, { roomName: roomname })
        }
        parentUpdateHandler.call(this, update, origin)
      }
      ;(this as any)._updateHandler = wrappedUpdateHandler
      this.doc.on('update', wrappedUpdateHandler)
    }

    // Set awareness state - ensure local state is initialized as an object first
    // This prevents the "Cannot read properties of undefined (reading 'clock')" error
    // by ensuring the awareness state exists before the websocket tries to encode it
    const currentState = this.awareness.getLocalState()
    if (!currentState || typeof currentState !== 'object') {
      this.awareness.setLocalState({})
    }
    this.awareness.setLocalStateField('room', roomname)
    this.awareness.setLocalStateField('memberId', memberId)

    this.autoConnect = options.connect !== false

    // Store original sync handler
    this.origSync = this.messageHandlers[messageSync]

    // Add custom message handlers
    this.messageHandlers[messageMemberId] =
      this.handleMemberIdMessage.bind(this)
    this.messageHandlers[messageHardReset] =
      this.handleHardResetMessage.bind(this)
    this.messageHandlers[messageEvent] = this.handleEventMessage.bind(this)
    this.messageHandlers[messagePong] = this.handlePongMessage.bind(this)
    this.messageHandlers[messageDamaged] = this.handleDamagedMessage.bind(this)
    this.messageHandlers[messageDataReceived] = this.handleDataReceivedMessage.bind(this)
    this.messageHandlers[messageRepairing] = this.handleRepairingMessage.bind(this)
    this.messageHandlers[messageConsistencyCheckOK] = this.handleConsistencyCheckOK.bind(this)
    this.messageHandlers[messageConsistencyCheckPatch] = this.handleConsistencyCheckPatch.bind(this)

    // Override the sync handler to gate SYNC_STEP_1 on the consistency check.
    // When the server's SYNC_STEP_1 arrives we buffer the server's state vector and
    // wait for IDB to load + consistency check to resolve before sending SYNC_STEP_2.
    // This prevents stale IDB values from overwriting correct server values via CRDT.
    this.messageHandlers[messageSync] = (encoder: any, decoder: any, _doc: any, emitSynced: any) => {
      const subtype = decoding.readVarUint(decoder)
      console.log(`[SyncGate] Received messageSync subtype=${subtype} for ${this.roomname} (idbSynced=${this._idbSynced}, gateActive=${this._syncGateActive})`)
      if (subtype === 0) {
        // SYNC_STEP_1 from server — read and hold the server's state vector
        const serverSV = decoding.readVarUint8Array(decoder)

        // During create-eval wait, skip gate activation entirely.
        // The gate exists to run consistency checks against IDB, which is meaningless
        // for brand-new evaluations. Each gate activation adds a 5s IDB timeout that
        // blocks sync progress and delays the Y.Map from populating.
        if (this._createEvalWaitTimer) {
          console.log(`[SyncGate] Skipping gate (create-eval wait active) — responding immediately for ${this.roomname}`)
          // Send SYNC_STEP_2 (give server what it's missing based on its SV)
          const step2Enc = encoding.createEncoder()
          encoding.writeVarUint(step2Enc, messageSync)
          syncProtocol.writeSyncStep2(step2Enc, this.doc, serverSV)
          this.ws!.send(encoding.toUint8Array(step2Enc))
          // Send SYNC_STEP_1 (ask server for what we're missing)
          const step1Enc = encoding.createEncoder()
          encoding.writeVarUint(step1Enc, messageSync)
          syncProtocol.writeSyncStep1(step1Enc, this.doc)
          this.ws!.send(encoding.toUint8Array(step1Enc))
          return
        }

        this._pendingServerSV = serverSV
        this._syncGateActive = true
        console.log(`[SyncGate] Buffered SYNC_STEP_1 (SV ${serverSV.length} bytes) — gate ACTIVE for ${this.roomname}`)
        if (this._idbSynced || !this._persistence) {
          // IDB already loaded (or no IDB) — send consistency check immediately
          this.sendConsistencyCheck()
        } else {
          // IDB not ready yet — wait; onIdbSynced() will trigger the check.
          // Safety: release gate after 5 s even if IDB never fires so sync can't hang forever.
          this._syncGateTimeout = setTimeout(() => {
            if (this._syncGateActive) {
              console.warn('[SyncGate] IDB never synced in time — releasing gate without consistency check')
              this._releaseSyncGate()
            }
          }, 5000)
        }
      } else {
        // SYNC_STEP_2 or YJS_UPDATE from server — apply immediately (always safe)
        // Track dataMap size before applying (lightweight — no key enumeration)
        const dataMapBefore = this.doc.share.has('data') ? this.doc.getMap('data') : null
        const dataMapSizeBefore = dataMapBefore ? dataMapBefore.size : 0

        syncProtocol.readSyncStep2(decoder, this.doc, this)

        // Warn if dataMap shrank during apply (possible phantom delete)
        const dataMapAfter = this.doc.share.has('data') ? this.doc.getMap('data') : null
        const dataMapSizeAfter = dataMapAfter ? dataMapAfter.size : 0
        if (dataMapSizeAfter < dataMapSizeBefore) {
          console.warn('⚠️ [Sync] dataMapSize DECREASED during readSyncStep2:', {
            before: dataMapSizeBefore,
            after: dataMapSizeAfter,
            delta: dataMapSizeBefore - dataMapSizeAfter,
            subtype,
            roomName: this.roomname,
          })
        }

        this.lastSyncComplete = Date.now()
        this._compactIdbOnRepair()
        if (!(this as any).synced) {
          // During create-eval wait, don't set synced until Y.Map is populated.
          // This keeps the status indicator on "Syncing" instead of prematurely showing "Updated".
          if (!this._createEvalWaitTimer) {
            // Setting synced via the setter emits both 'synced' and 'sync' events.
            // Note: emitSynced is a boolean flag in this y-websocket version, not a function.
            ;(this as any).synced = true
          }
        }
      }
    }

    if (this.autoConnect) {
      this.on('status', (statusArray: string[]) => {
        const status = statusArray?.[0] || ''
        if (status === 'disconnected' && this.shouldReconnect) {
          this.reconnect()
        }
      })
      this.connect()
    }
  }

  /**
   * Start health monitoring to detect zombie connections
   * Only checks for truly dead connections (no messages including ping/pong)
   * Sync issues are handled by the sync verification timeouts in the open handler
   */
  private startHealthCheck() {
    // Clean up any existing health check
    this.stopHealthCheck()

    this.healthCheckInterval = setInterval(() => {
      const now = Date.now()
      const timeSinceLastMessage = now - this.lastMessageTime

      // Only check: If WebSocket is "connected" but we haven't received ANY messages
      // (including pings) in MESSAGE_TIMEOUT_MS, the connection is dead
      // We DON'T force reconnects for sync issues here - that's handled elsewhere
      if (this.wsconnected && timeSinceLastMessage > this.MESSAGE_TIMEOUT_MS) {
        console.error(
          `[HealthCheck] No messages received in ${this.MESSAGE_TIMEOUT_MS}ms despite wsconnected=true`,
          {
            timeSinceLastMessage,
            roomName: this.roomname,
            note: 'Connection appears dead, will let close handler trigger reconnect'
          }
        )
        // Just close the WebSocket - don't modify wsconnected directly
        // The close event handler will set wsconnected=false and trigger reconnect if needed
        this.ws?.close()
        return
      }
    }, 15000) // Check every 15 seconds (less aggressive)

    console.log('[HealthCheck] Started monitoring', {
      roomName: this.roomname,
      checkIntervalMs: 15000,
      messageTimeoutMs: this.MESSAGE_TIMEOUT_MS
    })
  }

  /**
   * Stop health monitoring
   */
  private stopHealthCheck() {
    if (this.healthCheckInterval) {
      clearInterval(this.healthCheckInterval)
      this.healthCheckInterval = null
      console.log('[HealthCheck] Stopped monitoring', {
        roomName: this.roomname,
      })
    }
  }

  handleMemberIdMessage(
    encoder: any,
    decoder: any,
    provider: any,
    emitSynced: any,
    messageType: any
  ) {
    console.log('Handling memberId message')
    this.sendMemberId()
    console.log('Handled memberId message')
  }

  handleHardResetMessage(
    encoder: any,
    decoder: any,
    provider: any,
    emitSynced: any,
    messageType: any
  ) {
    console.log('Received hard reset command')
    this.performHardReset(this.doc)
  }

  handleEventMessage(encoder: any, decoder: any) {
    try {
      console.log('1. Starting event message decode for room:', this.roomname)

      // Skip message type as it's already been read
      console.log('2. Reading message length (var-length encoded)')
      let length = 0
      let shift = 0
      let byte
      do {
        byte = decoding.readUint8(decoder)
        length |= (byte & 0x7f) << shift
        shift += 7
      } while (byte & 0x80)

      console.log('3. Length decoded:', length)

      // Read exactly that many bytes for the message
      console.log('4. Reading message content')
      const messageBytes = new Uint8Array(length)
      for (let i = 0; i < length; i++) {
        messageBytes[i] = decoding.readUint8(decoder)
      }

      const message = new TextDecoder().decode(messageBytes)
      console.log('5. Message decoded:', message)

      // Parse the pipe-delimited message
      const parts = message.split('|')
      if (parts.length >= 3) {
        const thirdPart = parts[2]
        const roomParts = thirdPart.split(':')
        const messageRoom = roomParts[0]
        if (messageRoom === this.roomname) {
          console.log(
            '6. Message is for current room, emitting:',
            this.roomname
          )
          this.emit('eventMessage', [message])
        } else {
          console.log('6. Message is for different room, skipping:', {
            messageRoom,
            currentRoom: this.roomname,
          })
        }
      } else {
        console.log('6. Invalid message format, skipping:', message)
      }
    } catch (err) {
      console.error('Error decoding broadcast message:', err)
      console.error('Raw decoder content:', new Uint8Array(decoder))
    }
  }

  handlePongMessage(_encoder: any, decoder: any) {
    const memberId = decoding.readVarString(decoder)
    if (memberId === this.memberId) {
      if (this.pendingPingTimeout) {
        clearTimeout(this.pendingPingTimeout)
        this.pendingPingTimeout = null
      }
      this.pingInFlight = false
      console.log(`[Ping] Pong received — connection confirmed alive for ${this.memberId}`)
    }
  }

  /**
   * Received from the server when it detects consecutive phantom-delete-only updates,
   * which indicates the client's local Y.Doc CRDT state is corrupted. Emit a 'damaged'
   * event so EvaluationProvider can trigger a hard reset (disconnect → clear IndexedDB
   * → reconnect from server's authoritative state).
   */
  handleDamagedMessage(_encoder: any, _decoder: any) {
    console.warn(
      `[DamagedDoc] Server signalled corrupted CRDT state for room: ${this.roomname}. ` +
      `Emitting 'damaged' event to trigger hard reset.`
    )
    // Discard any pending sync — don't let stale IDB data propagate to server
    this._pendingServerSV = null
    this._syncGateActive = false
    if (this._syncGateTimeout) {
      clearTimeout(this._syncGateTimeout)
      this._syncGateTimeout = null
    }
    this.emit('damaged', [{ roomname: this.roomname, memberId: this.memberId }])
  }

  handleRepairingMessage(_encoder: any, _decoder: any) {
    console.log(`[Repair] Server signalled repair in progress for room: ${this.roomname}.`)
    // Mirror the Redis compaction done by file-loader: after the repair syncs fresh state,
    // clear stale IDB so clients don't carry bloated CRDT history into future page loads.
    this._compactIdbAfterSync = true
    this.emit('repairing', [{ roomname: this.roomname }])
  }

  /**
   * If a repair just completed (flagged by handleRepairingMessage), clear the IDB store.
   * This mirrors the Redis compaction done in file-loader after a repair cycle: the server
   * already has a clean single-entry Redis state; clients should have a clean IDB too so
   * future page loads don't forward a bloated CRDT state dump before sync completes.
   *
   * After clearData(), IDB is empty. The Y.Doc already has the fresh server state in memory.
   * Subsequent user edits will repopulate IDB as small incremental entries.
   */
  private _compactIdbOnRepair() {
    if (!this._compactIdbAfterSync) return
    this._compactIdbAfterSync = false
    const persistence = this._persistence
    if (!persistence) {
      console.debug('[Repair] IDB compact skipped — no persistence attached', { roomName: this.roomname })
      return
    }
    persistence.clearData().then(() => {
      console.log('[Repair] IDB compacted after repair — stale CRDT history cleared', { roomName: this.roomname })
    }).catch((err: Error) => {
      console.warn('[Repair] IDB compact failed:', err.message)
    })
  }

  /**
   * Received when y-websocket's data-observer confirms it received a data-map change
   * from this client. Fires before the debounce window and regardless of whether the
   * change will pass BindLock filters on the evaluation-update side.
   * Only sent in 'each' mode. Payload: room (string) + memberId (string).
   */
  handleDataReceivedMessage(_encoder: any, decoder: any) {
    const room = decoding.readVarString(decoder)
    const memberId = decoding.readVarString(decoder)
    if (memberId !== this.memberId) return // safety: not for this client
    this.lastDataAckTime = Date.now()
    console.log(`[DataReceived] y-websocket confirmed data-map receipt for room=${room}`)
    this.emit('dataReceived', [{ room, memberId, timestamp: this.lastDataAckTime }])
  }

  /**
   * Server confirmed the post-sync consistency check passed — hashes match.
   * Signal that the collaboration hold can be released (evaluation back to normal mode).
   */
  handleConsistencyCheckOK(_encoder: any, _decoder: any) {
    console.log(`[ConsistencyCheck] Server confirmed OK for ${this.roomname} — releasing sync gate`)
    this._stopDirtyKeysTracking()
    this.emit('consistencyCheckOK', [{ roomname: this.roomname }])
    this._releaseSyncGate()
  }

  /**
   * Server determined that some keys differ between client and server.
   * Apply the authoritative values directly to the local Y.Doc before sync runs so the
   * correct values win the CRDT merge. Then release the sync gate.
   */
  handleConsistencyCheckPatch(_encoder: any, decoder: any) {
    const count = decoding.readVarUint(decoder)
    if (count > 0) {
      const dataMap = this.doc.getMap<string>('data')
      const patchedKeys: string[] = []
      const dirtyKeys = this._dirtyKeysSinceCheck
      let skippedCount = 0
      this.doc.transact(() => {
        for (let i = 0; i < count; i++) {
          const key = decoding.readVarString(decoder)
          const value = decoding.readVarString(decoder)
          // Don't overwrite keys the user modified during the round-trip
          if (dirtyKeys?.has(key)) {
            skippedCount++
            continue
          }
          patchedKeys.push(key)
          dataMap.set(key, value)
        }
      }, 'consistency-patch')
      this._stopDirtyKeysTracking()
      if (skippedCount > 0) {
        console.log(`[ConsistencyCheck] Applied selective patch (${patchedKeys.length} key(s), skipped ${skippedCount} locally-modified) for ${this.roomname}`)
      } else {
        console.log(`[ConsistencyCheck] Applied selective patch (${count} key(s)) for ${this.roomname}`)
      }

      // Verify how many patched keys actually survived local CRDT tombstones.
      // If the client has stale DELETE tombstones in IDB, .set() above will be
      // immediately overridden by those tombstones — the keys won't be readable.
      const survivedCount = patchedKeys.filter(k => dataMap.has(k)).length
      const suppressedCount = count - survivedCount
      if (suppressedCount > 0) {
        console.warn(
          `[ConsistencyCheck] ${suppressedCount}/${count} patched key(s) suppressed by local CRDT tombstones`,
          { roomName: this.roomname, survivedCount, suppressedCount }
        )
      }

      if (suppressedCount > 0 && !this._hasTriggeredSelfRepair) {
        this._hasTriggeredSelfRepair = true
        console.warn(
          `[ConsistencyCheck] CRDT state corrupted — ${suppressedCount} key(s) cannot be restored over tombstones. Triggering self-repair (clear IDB + reconnect).`,
          { roomName: this.roomname }
        )
        // Do NOT release sync gate — self-repair will clear IDB and reconnect fresh
        this.handleDamagedMessage(null, null)
        return
      }
    }
    this.emit('consistencyCheckOK', [{ roomname: this.roomname }])
    this._releaseSyncGate()
  }

  /**
   * Called by EvaluationProvider when IndexedDB has finished loading into the Y.Doc.
   * If a SYNC_STEP_1 from the server is pending (sync gate is active), triggers the
   * consistency check now that we have the IDB data to compare against.
   */
  onIdbSynced(): void {
    this._idbSynced = true
    if (this._syncGateTimeout) {
      clearTimeout(this._syncGateTimeout)
      this._syncGateTimeout = null
    }
    console.log(`[SyncGate] onIdbSynced — gateActive=${this._syncGateActive}, pendingSV=${this._pendingServerSV !== null ? 'set' : 'null'} for ${this.roomname}`)
    if (this._syncGateActive && this._pendingServerSV !== null) {
      this.sendConsistencyCheck()
    }
  }

  /**
   * Release the sync gate: respond to the server's buffered SYNC_STEP_1 with our
   * SYNC_STEP_2 (give server what it's missing) and send our own SYNC_STEP_1
   * (ask server for what we're missing). Safe to call even if no gate is active.
   */
  private _releaseSyncGate(): void {
    if (!this._syncGateActive || this._pendingServerSV === null) {
      console.log(`[SyncGate] _releaseSyncGate early return — gateActive=${this._syncGateActive}, pendingSV=${this._pendingServerSV !== null ? 'set(' + this._pendingServerSV.length + 'b)' : 'null'} for ${this.roomname}`)
      return
    }
    const serverSV = this._pendingServerSV
    this._pendingServerSV = null
    this._syncGateActive = false
    if (this._syncGateTimeout) {
      clearTimeout(this._syncGateTimeout)
      this._syncGateTimeout = null
    }
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      console.warn(`[SyncGate] _releaseSyncGate WS not open — gate cleared but SYNC_STEP_2 NOT sent for ${this.roomname}`)
      return
    }

    // SYNC_STEP_2: send server what it's missing based on its own state vector
    const step2Encoder = encoding.createEncoder()
    encoding.writeVarUint(step2Encoder, messageSync)
    syncProtocol.writeSyncStep2(step2Encoder, this.doc, serverSV)
    const step2Msg = encoding.toUint8Array(step2Encoder)
    console.log(`[SyncGate] Sending SYNC_STEP_2 (${step2Msg.length} bytes) for ${this.roomname}`)
    this.ws.send(step2Msg)

    // SYNC_STEP_1: ask server for what we're missing
    const step1Encoder = encoding.createEncoder()
    encoding.writeVarUint(step1Encoder, messageSync)
    syncProtocol.writeSyncStep1(step1Encoder, this.doc)
    const step1Msg = encoding.toUint8Array(step1Encoder)
    console.log(`[SyncGate] Sending SYNC_STEP_1 (${step1Msg.length} bytes) for ${this.roomname}`)
    this.ws.send(step1Msg)

    console.log(`[SyncGate] Released — sync proceeding for ${this.roomname}`)
  }

  /**
   * Compute a SHA-256 hash of ALL data map key-value pairs in sorted order.
   * Used to detect stale client-side CRDT state after sync and during periodic polling.
   */
  async computeDataMapHash(): Promise<string> {
    const dataMap = this.doc.getMap<string>('data')
    const entries: string[] = []
    dataMap.forEach((val: string, key: string) => {
      entries.push(`${key}=${val}`)
    })
    entries.sort()
    const text = entries.join('\n')
    const buffer = new TextEncoder().encode(text)
    const hashBuffer = await crypto.subtle.digest('SHA-256', buffer)
    const hashArray = Array.from(new Uint8Array(hashBuffer))
    return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('')
  }

  /**
   * Send a consistency check to the server. Server will compare its data map
   * hash with the client hash embedded in the message. If they differ, server
   * sends a selective patch to correct the client before CRDT sync runs.
   *
   * Call this once after initial sync + a settling delay, before any user edits.
   */
  async sendConsistencyCheck(): Promise<void> {
    if (!this.wsconnected || !this.ws || this.ws.readyState !== WebSocket.OPEN) {
      // Gate must always release — even if WS isn't open, release so the next
      // connection cycle can send SYNC_STEP_2 and trigger server self-heal.
      console.warn('[ConsistencyCheck] WS not open — releasing gate without check')
      this._releaseSyncGate()
      return
    }

    // For brand new evaluations (create flow), skip the consistency check.
    // The create state is tracked via sessionStorage by EvaluationCreateView.
    // No stale IDB data exists for new evaluations — normal Y.js sync is sufficient.
    // Sending the consistency check for an empty Y.Map would cause a guaranteed hash
    // mismatch → server patches ALL 9000+ keys → creates browser-owned CRDT Items →
    // dual-ownership → subsequent edits misclassified as DELETE on the server.
    const isCreateEvaluation = typeof window !== 'undefined' &&
      sessionStorage.getItem('evaluation-create-session-id') !== null

    if (isCreateEvaluation) {
      const dataMap = this.doc.getMap<string>('data')
      console.log(`[ConsistencyCheck] Create evaluation detected — releasing gate (dataMap.size=${dataMap.size}) for ${this.roomname}`)
      this._releaseSyncGate()
      this._startCreateEvalWaitLoop()
      return
    }

    const dataMap = this.doc.getMap<string>('data')

    // Start tracking local modifications so the patch won't overwrite user changes
    // made during the round-trip.
    this._stopDirtyKeysTracking()
    this._dirtyKeysSinceCheck = new Set<string>()
    this._dirtyKeysObserver = (event: Y.YMapEvent<string>) => {
      if (event.transaction.origin === 'consistency-patch') return
      for (const key of event.keysChanged) {
        this._dirtyKeysSinceCheck?.add(key)
      }
    }
    dataMap.observe(this._dirtyKeysObserver)

    const clientHash = await this.computeDataMapHash()
    // Include the client's Y.js state vector so the server can do per-key freshness
    // comparison and send a selective patch instead of a full repair on mismatch.
    const clientStateVector = Y.encodeStateVector(this.doc)
    const encoder = encoding.createEncoder()
    encoding.writeVarUint(encoder, messageConsistencyCheck)
    encoding.writeVarString(encoder, clientHash)
    encoding.writeVarUint8Array(encoder, clientStateVector)
    this.ws.send(encoding.toUint8Array(encoder))
    console.log(`[ConsistencyCheck] Sent check for ${this.roomname} hash=${clientHash.slice(0, 8)} svSize=${clientStateVector.length}`)
  }

  private _stopDirtyKeysTracking() {
    if (this._dirtyKeysObserver) {
      const dataMap = this.doc.getMap<string>('data')
      dataMap.unobserve(this._dirtyKeysObserver)
      this._dirtyKeysObserver = null
    }
    this._dirtyKeysSinceCheck = null
  }

  /**
   * For new evaluations: cycle reconnect until ydoc-service populates the Y.Map.
   * On first connection the server may not have data yet — keep trying.
   */
  private _startCreateEvalWaitLoop(): void {
    if (this._createEvalWaitTimer) return

    let attempts = 0
    const maxAttempts = 30 // 30 × 2s = 60s max

    this._createEvalWaitTimer = setInterval(() => {
      attempts++
      const dataMap = this.doc.getMap<string>('data')

      if (dataMap.size > 0) {
        console.log(`[CreateEvalWait] Y.Map populated (${dataMap.size} keys) after ${attempts} checks for ${this.roomname}`)
        this._stopCreateEvalWaitLoop()
        // Now that data is present, mark as synced — this transitions the status to "Updated"
        if (!(this as any).synced) {
          ;(this as any).synced = true
        }
        return
      }

      if (attempts >= maxAttempts) {
        console.warn(`[CreateEvalWait] Y.Map still empty after ${maxAttempts} checks — stopping for ${this.roomname}`)
        this._stopCreateEvalWaitLoop()
        // Set synced so status doesn't stay stuck on "Syncing" forever
        if (!(this as any).synced) {
          ;(this as any).synced = true
        }
        return
      }

      // Re-send sync step 1 to ask server for latest state without disconnecting.
      // This avoids UI status flicker (Disconnected → Syncing → Disconnected).
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        console.log(`[CreateEvalWait] Y.Map still empty (attempt ${attempts}/${maxAttempts}) — re-syncing for ${this.roomname}`)
        this.sendSync()
      } else {
        console.log(`[CreateEvalWait] Y.Map still empty (attempt ${attempts}/${maxAttempts}) — WS not open, reconnecting for ${this.roomname}`)
        this.disconnect()
        setTimeout(() => this.connect(), 500)
      }
    }, 2000)
  }

  private _stopCreateEvalWaitLoop(): void {
    if (this._createEvalWaitTimer) {
      clearInterval(this._createEvalWaitTimer)
      this._createEvalWaitTimer = null
    }
  }

  /**
   * Send a ping to the server and wait up to 1 second for a pong response.
   * If no pong arrives, the connection is treated as half-open and reconnect is forced.
   * Uses an in-flight guard so only one ping is pending at a time.
   */
  verifyConnection(): void {
    // Only one ping at a time
    if (this.pingInFlight) return

    // Socket is obviously dead — reconnect immediately, no need to ping
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      console.warn(
        `[Ping] WebSocket not open on user change (readyState=${this.ws?.readyState}), forcing reconnect`
      )
      this.reconnect()
      return
    }

    this.pingInFlight = true

    const encoder = encoding.createEncoder()
    encoding.writeVarUint(encoder, messagePing)
    encoding.writeVarString(encoder, this.memberId)
    this.ws.send(encoding.toUint8Array(encoder))

    console.log(`[Ping] Sent ping for ${this.memberId}`)

    // If no pong within 1 second, connection is half-open.
    // Close the WebSocket directly — the close event handler calls reconnect()
    // automatically, bypassing the readyState===OPEN guard that would otherwise
    // prevent reconnect() from acting when the socket appears open but is dead.
    this.pendingPingTimeout = setTimeout(() => {
      this.pingInFlight = false
      this.pendingPingTimeout = null
      console.warn(
        `[Ping] No pong within 1s for ${this.memberId} — connection appears half-open, closing WebSocket`
      )
      if (this.ws) {
        this.ws.close()
      }
    }, 1000)
  }

  sendMemberId() {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      console.log('Preparing to send memberId for room:', this.roomname)
      const encoder = encoding.createEncoder()
      encoding.writeVarUint(encoder, messageMemberId)
      encoding.writeVarString(encoder, this.memberId)
      encoding.writeVarString(encoder, ':')
      encoding.writeVarString(encoder, this.roomname)
      const message = encoding.toUint8Array(encoder)
      this.ws.send(message)
      console.log('Sent memberId for room:', this.roomname)
    } else {
      console.log('WebSocket not ready for memberId transmission.')
    }
  }

  performHardReset = async (doc: CustomDoc) => {
    console.log('Performing hard reset...')

    doc.transact(() => {
      for (let [key, value] of doc.share.entries()) {
        if (value instanceof Y.Map) {
          value.clear()
        } else if (value instanceof Y.Array) {
          value.delete(0, value.length)
        }
      }
    })

    try {
      const idbPersistence = new IndexeddbPersistence(doc.guid, doc)
      await new Promise<void>((resolve, reject) => {
        idbPersistence.once('synced', () => {
          idbPersistence
            .clearData()
            .then(() => {
              console.log('IndexedDB data cleared successfully')
              resolve()
            })
            .catch((error) => {
              console.error('Error clearing IndexedDB data:', error)
              reject(error)
            })
            .finally(() => {
              idbPersistence.destroy()
            })
        })
      })
    } catch (error) {
      console.error('Error during IndexedDB operation:', error)
    }
    console.log('Hard reset completed')
  }

  connect() {
    // CRITICAL: Always set shouldReconnect to true when connecting
    // This ensures that even if something set it to false (e.g., cleanup from previous render),
    // it will be true when we actually try to connect
    const wasFalse = !this.shouldReconnect
    this.shouldReconnect = true
    if (wasFalse) {
      console.warn('[connect] shouldReconnect was false, resetting to true', {
        roomName: this.roomname,
        docGuid: this.doc?.guid,
        stack: new Error().stack,
      })
    } else {
      console.log('[connect] Setting shouldReconnect to true (was already true)', {
        roomName: this.roomname,
        docGuid: this.doc?.guid,
      })
    }
    
    // Guard against multiple simultaneous connection attempts
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      console.log('WebSocket is already open for room:', this.roomname)
      this.isReconnecting = false // Reset flag if already connected
      return
    }

    if (this.ws && this.ws.readyState === WebSocket.CONNECTING) {
      console.log(
        'WebSocket is currently connecting for room:',
        this.roomname
      )
      return
    }

    if (this.ws && this.ws.readyState === WebSocket.CLOSING) {
      // WebSocket is in CLOSING state — it hasn't fully closed yet.
      // Returning here (as the old code did) leaves the provider permanently disconnected
      // when connect() is called immediately after disconnect() (e.g., in handleDamagedReset).
      // Instead, schedule connect() to retry once the socket is actually CLOSED.
      console.log(
        '[connect] WebSocket is CLOSING — scheduling reconnect after close for room:',
        this.roomname
      )
      this.ws.addEventListener('close', () => this.connect(), { once: true })
      return
    }

    // If we're already reconnecting and there's a websocket in progress, don't start another
    if (
      this.isReconnecting &&
      this.ws &&
      this.ws.readyState !== WebSocket.CLOSED
    ) {
      console.log(
        'Connection already in progress, skipping duplicate connect() call'
      )
      return
    }

    // Clean up old connection in YDoc's conns map
    if (this.ws) {
      console.log(
        "Cleaning up old connection in YDoc's conns map for room:",
        this.roomname
      )
      console.log(
        'Current connections in YDoc:',
        Array.from(this.doc.conns.keys()).map((conn) => ({
          readyState: conn.readyState,
          isCurrentWs: conn === this.ws,
          room: this.roomname,
        }))
      )

      this.doc.conns.forEach((_, conn) => {
        if (conn === this.ws) {
          console.log(
            "Removing old connection from YDoc's conns map for room:",
            this.roomname
          )
          this.doc.conns.delete(conn)
        }
      })

      console.log(
        'Connections after cleanup:',
        Array.from(this.doc.conns.keys()).map((conn) => ({
          readyState: conn.readyState,
          isCurrentWs: conn === this.ws,
          room: this.roomname,
        }))
      )

      // Properly close the old WebSocket
      try {
        this.ws.close()
      } catch (e) {
        console.warn('Error closing old WebSocket:', e)
      }
      this.ws = null
    }

    // Ensure awareness is properly initialized before connecting
    // This prevents the "Cannot read properties of undefined (reading 'clock')" error
    // by ensuring awareness state exists and is registered before the websocket tries to encode it
    const localState = this.awareness.getLocalState()
    if (!localState || typeof localState !== 'object') {
      this.awareness.setLocalState({
        room: this.roomname,
        memberId: this.memberId,
      })
    } else {
      // Ensure fields are set even if state exists
      this.awareness.setLocalStateField('room', this.roomname)
      this.awareness.setLocalStateField('memberId', this.memberId)
    }

    // Force awareness to register the state by triggering an update
    // This ensures the state is in the awareness map before encodeAwarenessUpdate is called
    const currentState = this.awareness.getLocalState()
    if (currentState) {
      // Trigger a state update to ensure it's registered in the awareness map
      this.awareness.setLocalState(currentState)
    }

    const fullUrl = `${this.url}${this.roomname}`
    console.log(
      'Initiating WebSocket connection for room:',
      this.roomname,
      'URL:',
      fullUrl
    )

    // Store reference to override onopen before super.connect() sets it up
    // We need to intercept the websocket creation to set up our handler first
    const originalConnect = super.connect.bind(this)

    // Call super.connect() which will create the websocket
    originalConnect()

    /**
     * salman/websocket-fixes
     *
     * Not removed it yet but here's what GPT suggests to do:
     * Because you now have explicit sendSync() / ensureSynced() logic,
     * that awareness hack is probably no longer necessary and might still be
     * disrupting the parent WebsocketProvider’s normal onopen logic in edge cases.
     * A safe simplification is:
     * - Remove the ws.onopen override entirely and let the stock WebsocketProvider handle awareness.
     * - Keep your explicit sync / ensureSynced logic as the main recovery path.
     */

    // Override the websocket's onopen handler immediately after connection setup
    // This must happen before the websocket actually opens to prevent race conditions
    if (this.ws) {
      // Store the original onopen if it exists
      // Type assertion needed because TypeScript doesn't narrow the type correctly
      const ws = this.ws as WebSocket
      const originalOnOpen = ws.onopen

      ws.onopen = (event: Event) => {
        // CRITICAL: The parent WebsocketProvider's onopen handler will try to encode awareness
        // It calls: encodeAwarenessUpdate(this.awareness, [this.clientID])
        // This will fail with "Cannot read properties of undefined (reading 'clock')" if
        // the state for clientID isn't registered in the awareness map.
        //
        // The issue: setLocalState() should register the state, but there might be a timing
        // issue where clientID isn't set yet, or the state isn't immediately available.
        //
        // Solution: Ensure awareness state is set and registered BEFORE calling the parent handler

        // First, ensure we have a valid state object
        let currentState = this.awareness.getLocalState()
        if (!currentState || typeof currentState !== 'object') {
          currentState = {
            room: this.roomname,
            memberId: this.memberId,
          }
        } else {
          // Ensure fields are set even if state exists
          this.awareness.setLocalStateField('room', this.roomname)
          this.awareness.setLocalStateField('memberId', this.memberId)
          currentState = this.awareness.getLocalState()
        }

        // Get the clientID - it should be set by the parent WebsocketProvider by now
        const clientID = (this as any).clientID

        // CRITICAL: Ensure the state is registered in the awareness map for this clientID
        // encodeAwarenessUpdate requires the state to exist in the map, not just as local state
        // The parent handler will call: encodeAwarenessUpdate(this.awareness, [this.clientID])
        // This will fail if the state for clientID isn't in the awareness map

        if (clientID !== undefined) {
          // Set the local state - this should register it in the awareness map synchronously
          this.awareness.setLocalState(currentState)

          // CRITICAL: Verify it's actually registered in the map BEFORE calling parent handler
          // If it's not registered, the parent's encodeAwarenessUpdate will fail
          const states = this.awareness.getStates()
          let registeredState = states.get(clientID)

          // If not registered, try setting it again - sometimes setLocalState needs to be called
          // when the clientID is already set to properly register it
          if (!registeredState) {
            console.warn(
              'Awareness state not registered for clientID, forcing registration:',
              clientID
            )
            // Force registration by setting state again
            this.awareness.setLocalState({
              room: this.roomname,
              memberId: this.memberId,
            })
            // Check again
            registeredState = this.awareness.getStates().get(clientID)
            if (!registeredState) {
              console.error(
                'Failed to register awareness state for clientID:',
                clientID
              )
              // This is a critical error - the parent handler will fail
            }
          }
        } else {
          // If clientID isn't set yet, this is a problem
          // The parent handler needs clientID to encode awareness
          console.warn(
            'clientID not set when websocket opens - awareness encoding may fail'
          )
          // Set the state anyway - it might get registered when clientID is set
          this.awareness.setLocalState(currentState)
        }

        // Now call the original onopen handler
        // The parent handler should now succeed because the state is registered
        if (originalOnOpen) {
          try {
            originalOnOpen.call(ws, event)
          } catch (error) {
            console.error('Error in websocket onopen handler:', error)
            // If it still fails, there's a deeper issue
            // Try one more time to register the state
            const clientID = (this as any).clientID
            if (clientID !== undefined) {
              this.awareness.setLocalState({
                room: this.roomname,
                memberId: this.memberId,
              })
              // Try calling the parent handler again
              try {
                originalOnOpen.call(ws, event)
              } catch (retryError) {
                console.error('Retry also failed:', retryError)
                // If both attempts fail, don't emit connected status
                // The connection isn't actually working properly
                // Let the connection fail naturally or handle reconnection elsewhere
              }
            }
            // If clientID is undefined or retry failed, don't emit false 'connected' status
            // The parent handler failed, so the connection isn't functional
          }
        } else {
          // No original handler, just emit status
          this.emit('status', ['connected'])
        }

        // CRITICAL: Ensure the document connection is established
        // The parent WebsocketProvider should add a connection to doc.conns when it connects
        // If doc.conns.size === 0, the document has no connection and cannot sync
        // We need to verify the connection is established after the websocket opens
        // Use a longer timeout to give the parent provider time to establish the connection
        // Clear any existing timeout to prevent multiple checks
        if (this.connectionCheckTimeout) {
          clearTimeout(this.connectionCheckTimeout)
        }
        this.connectionCheckTimeout = setTimeout(() => {
          // Only check if websocket is still open and we're not already reconnecting
          if (
            this.ws &&
            this.ws.readyState === WebSocket.OPEN &&
            !this.isReconnecting
          ) {
            const providerSynced = (this as any).synced
            const connsSize = this.doc.conns.size

            // Check both provider.synced and doc.conns.size
            // provider.synced is the authoritative source, but doc.conns.size can be 0 even when synced
            if (!providerSynced && connsSize === 0) {
              console.warn(
                'Document not synced after websocket open. Attempting to sync...',
                {
                  room: this.roomname,
                  wsReadyState: this.ws?.readyState,
                  wsconnected: this.wsconnected,
                  providerSynced: providerSynced,
                  connsSize: connsSize,
                }
              )
              // Try sending a sync message - the parent provider should handle adding to doc.conns
              this.sendSync()
              // Reset reconnecting flag since we're just trying to sync, not reconnect
              this.isReconnecting = false
            } else {
              console.log('Document connection status verified', {
                room: this.roomname,
                providerSynced: providerSynced,
                connsSize: connsSize,
                wsReadyState: this.ws?.readyState,
              })
              // Connection exists or synced, reset reconnecting flag
              this.isReconnecting = false
              // If not synced yet but has connections, try to sync
              if (!providerSynced && connsSize > 0) {
                this.sendSync()
              }
            }
          }
          this.connectionCheckTimeout = null
        }, 500) // Increased timeout to give parent provider more time to establish connection
      }
    }

    this.setupListeners()

    // Add explicit sync after connection
    this.once('status', (statusArray: string[]) => {
      const status = statusArray?.[0] || ''
      if (status === 'connected') {
        console.log(
          'WebSocket connected for room:',
          this.roomname,
          'triggering YDoc sync...'
        )
        // Force a sync by sending sync step 1
        this.sendSync()
      }
    })

    // Set up connection timeout
    const timeout = 10000 // 10 seconds
    const timeoutId = setTimeout(() => {
      if (this.ws && this.ws.readyState !== WebSocket.OPEN) {
        console.log(
          'Connection timeout for room:',
          this.roomname,
          'attempting reconnect...'
        )
        this.reconnect()
      }
    }, timeout)

    // Clear timeout on successful connection
    this.once('status', ({ status }: { status: string }) => {
      if (status === 'connected') {
        clearTimeout(timeoutId)
      }
    })
  }

  setupListeners() {
    if (!this.ws) {
      console.warn('WebSocket not available for setting up listeners')
      return
    }

    // Wrap onmessage to catch "Unable to compute message" errors and convert to warnings
    // The parent WebsocketProvider sets up onmessage, so we need to intercept it
    const originalOnMessage = this.ws.onmessage
    this.ws.onmessage = (event: MessageEvent) => {
      // Track message receipt time (including ping/pong frames)
      this.lastMessageTime = Date.now()

      try {
        // Call the original handler (set by parent WebsocketProvider)
        if (originalOnMessage) {
          originalOnMessage.call(this.ws, event)
        }
      } catch (error: any) {
        // Catch "Unable to compute message" errors and convert to warnings
        const errorMessage = error?.message || String(error)
        if (
          errorMessage.includes('Unable to compute') ||
          errorMessage.includes('compute message')
        ) {
          console.warn(
            'Unable to compute message (benign, likely malformed message):',
            {
              room: this.roomname,
              error: errorMessage,
              dataLength:
                event.data?.byteLength || event.data?.length || 'unknown',
            }
          )
          // Don't re-throw - this is a benign error
        } else {
          // Re-throw other errors
          console.error('WebSocket message error:', error)
          throw error
        }
      }
    }

    // Handle WebSocket events
    this.ws.addEventListener('open', () => {
      console.log('WebSocket opened')
      // CRITICAL: Update wsconnected to true when WebSocket opens
      // This ensures the property accurately reflects the connection state
      this.wsconnected = true
      // Reset reconnecting flag when websocket successfully opens
      this.isReconnecting = false
      this.lastConnectTime = Date.now()
      this.lastMessageTime = Date.now()
      this.emit('status', ['connected'])

      // Start health monitoring
      this.startHealthCheck()
    })

    this.ws.addEventListener('close', (event) => {
      console.log('WebSocket closed:', event.code, event.reason)
      console.log('[WebSocket Close] shouldReconnect:', this.shouldReconnect, {
        isReconnecting: this.isReconnecting,
        wsReadyState: this.ws?.readyState,
        roomName: this.roomname,
      })
      // Stop health monitoring when connection closes
      this.stopHealthCheck()

      // CRITICAL: Update wsconnected to false when WebSocket closes
      // This prevents updates from being sent when WebSocket is actually closed
      this.wsconnected = false
      // Reset synced flag when disconnected
      ;(this as any).synced = false
      this.emit('status', ['disconnected'])
      if (this.shouldReconnect) {
        console.log('[WebSocket Close] Calling reconnect()...')
        this.reconnect()
      } else {
        console.warn('[WebSocket Close] NOT reconnecting because shouldReconnect is false')
      }
    })

    this.ws.addEventListener('error', (error) => {
      console.error('WebSocket error:', error)
      // Stop health monitoring on error
      this.stopHealthCheck()

      // CRITICAL: Update wsconnected to false on error
      // Errors typically indicate the connection is broken
      this.wsconnected = false
      this.emit('status', ['error'])
    })

    // NOTE: The sync message handler is set in the constructor (sync gate handler).
    // Do NOT override messageHandlers[messageSync] here — doing so would bypass the
    // sync gate that prevents stale IDB data from corrupting the server via CRDT merge.
  }

  sendSync() {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      console.log('Sending sync step 1...')
      const encoder = encoding.createEncoder()
      encoding.writeVarUint(encoder, messageSync)
      syncProtocol.writeSyncStep1(encoder, this.doc)
      const message = encoding.toUint8Array(encoder)
      this.ws.send(message)
    } else {
      console.warn('Cannot send sync - WebSocket not ready')
    }
  }

  /**
   * Check if the Y.js document is actually synced
   * Uses provider.synced which is set by the parent WebsocketProvider when sync completes
   * Also checks IndexedDB persistence if available
   * Note: doc.conns.size can be 0 even when synced (e.g., synced via IndexedDB)
   */
  isDocSynced(): boolean {
    /**
     * salman/websocket-fixes
     * Since in the EvaluationProvier, we use this flag to call ensureSynced like the following:
     * 
     *   if (isIndexedDB && provider) {
     *      ...
     *      const isDocSynced = provider.isDocSynced?.() ?? false
     *      if (!isDocSynced) {
     *        provider.ensureSynced()
     *      }
     *    }
     * 
     * If persistenceSynced is true, isDocSynced() returns true
     * even if the websocket handshake failed or is in a bad state,
     * and we never call ensureSynced()

     * Therefore it is important to make isDocSynced reflect websocket / server
     * sync only, and keep persistence state purely informational:
     */

    // // First check provider.synced - this is set when websocket sync completes
    // const providerSynced = (this as any).synced === true

    // // Check if IndexedDB persistence is synced (stored in EvaluationProvider)
    // const persistence = (this as any)._persistence
    // const persistenceSynced = persistence?.synced === true

    // // Check if document has active connections
    // const hasConnections = this.doc.conns.size > 0

    // // Document is synced if:
    // // 1. Provider is synced (websocket sync completed), OR
    // // 2. IndexedDB is synced (local persistence is ready), OR
    // // 3. Document has active connections (provider is in conns map)
    // return providerSynced || persistenceSynced || hasConnections

    // Check provider.synced - this is set when websocket sync completes
    const providerSynced = this.synced === true

    return providerSynced
  }

  /**
   * Check if both websocket is connected AND document is synced
   */
  isFullyConnected(): boolean {
    return (this.wsconnected ?? false) && this.isDocSynced()
  }

  /**
   * Get full connection status information for logging/debugging
   */
  getFullConnectionStatus(): {
    wsConnected: boolean
    wsReadyState: number | null
    docSynced: boolean
    fullyConnected: boolean
    hasConnections: boolean
    providerSynced: boolean
    persistenceSynced: boolean
  } {
    const providerSynced = (this as any).synced === true
    const persistence = (this as any)._persistence
    const persistenceSynced = persistence?.synced === true

    return {
      wsConnected: this.wsconnected ?? false,
      wsReadyState: this.ws?.readyState ?? null,
      docSynced: this.isDocSynced(),
      fullyConnected: this.isFullyConnected(),
      hasConnections: this.doc.conns.size > 0,
      providerSynced,
      persistenceSynced,
    }
  }

  /**
   * Ensure the document is synced. If websocket is connected but doc isn't synced,
   * attempt to resync by sending a sync message. If doc.conns is empty, reconnect.
   * Guards against multiple simultaneous reconnection attempts.
   */
  ensureSynced(): void {
    // Don't do anything if already reconnecting or if websocket is not connected
    if (this.isReconnecting || !this.wsconnected) {
      return
    }

    // Don't do anything if websocket is connecting or closing
    if (
      this.ws &&
      (this.ws.readyState === WebSocket.CONNECTING ||
        this.ws.readyState === WebSocket.CLOSING)
    ) {
      return
    }

    // Double-check sync status - it might have changed since the check above
    if (this.isDocSynced()) {
      // Document is synced, no need to do anything
      return
    }

    const providerSynced = (this as any).synced
    const persistence = this._persistence
    const persistenceSynced = persistence?.synced === true
    const now = Date.now()

    // If sync just completed recently, don't warn - give it time to propagate
    const timeSinceLastSync = now - this.lastSyncComplete
    if (timeSinceLastSync < this.SYNC_COMPLETE_GRACE_MS) {
      // Sync just completed, don't spam warnings
      return
    }

    /**
     * salman/websocket-fixes
     *
     * Problematic scenario:
     * Existing evaluation → IndexedDB is synced (persistenceSynced true).
     * Websocket is connected but provider is not properly synced / doc.conns is empty.
     * ensureSynced() returns immediately, never calling sendSync() or attempting recovery.
     *
     * Fix: Remove the early-return and let ensureSynced() actually do its job when providerSynced is false, regardless of persistenceSynced
     */

    // // If IndexedDB is synced, we're good - no need to sync via websocket
    // // The document has local data and will sync when websocket connects properly
    // if (persistenceSynced) {
    //   // IndexedDB is synced, so document has data locally
    //   // We just need websocket to connect for real-time updates
    //   // Don't spam sync attempts or warnings if IndexedDB is handling it
    //   return
    // }

    // Throttle sync attempts to avoid spam
    const shouldAttemptSync = now - this.lastSyncAttempt > this.SYNC_THROTTLE_MS
    if (!shouldAttemptSync) {
      return // Skip this attempt, we just tried recently
    }

    // Throttle warnings to avoid spam - only log if we're actually going to attempt something
    const shouldLogWarning =
      now - this.lastWarningTime > this.WARNING_THROTTLE_MS

    // Only log warnings and attempt sync if we're actually in a problematic state
    // If provider.synced is false/undefined and doc.conns is empty, try to sync
    if (!providerSynced && this.doc.conns.size === 0) {
      // Only attempt if websocket is open and we're not already reconnecting
      if (
        !this.isReconnecting &&
        this.ws &&
        this.ws.readyState === WebSocket.OPEN
      ) {
        this.lastSyncAttempt = now
        // Only log warning if throttling allows it
        if (shouldLogWarning) {
          this.lastWarningTime = now
          console.warn(
            'Document not synced and no connections. Attempting to sync...',
            {
              room: this.roomname,
              wsReadyState: this.ws?.readyState,
              providerSynced: providerSynced,
              persistenceSynced: persistenceSynced,
              connsSize: this.doc.conns.size,
              isReconnecting: this.isReconnecting,
            }
          )
        }
        // Try sending a sync message
        this.sendSync()
      }
    } else if (!providerSynced && this.doc.conns.size > 0) {
      // Document has connections but provider.synced is false - try sending sync
      // This is less critical, so only log if warning throttle allows
      this.lastSyncAttempt = now
      if (shouldLogWarning) {
        this.lastWarningTime = now
        console.warn(
          'WebSocket is connected but document is not synced. Attempting to sync...',
          {
            room: this.roomname,
            wsReadyState: this.ws?.readyState,
            providerSynced: providerSynced,
            persistenceSynced: persistenceSynced,
            connsSize: this.doc.conns.size,
            isReconnecting: this.isReconnecting,
          }
        )
      }
      this.sendSync()
    }
    // If none of the above conditions are met, silently return (no warning needed)
  }

  reconnect() {
    console.log('[reconnect] Called', {
      isReconnecting: this.isReconnecting,
      shouldReconnect: this.shouldReconnect,
      wsReadyState: this.ws?.readyState,
      isDocSynced: this.isDocSynced(),
      roomName: this.roomname,
    })
    
    // Prevent multiple simultaneous reconnection attempts
    if (this.isReconnecting) {
      console.log('[reconnect] Reconnection already in progress, skipping...')
      return
    }

    // Don't reconnect if websocket is already open and document is synced
    if (
      this.ws &&
      this.ws.readyState === WebSocket.OPEN &&
      this.isDocSynced()
    ) {
      console.log(
        '[reconnect] WebSocket is open and document is synced, no need to reconnect'
      )
      return
    }

    this.isReconnecting = true
    console.log('[reconnect] Attempting to reconnect...')

    if (this.ws) {
      try {
        this.ws.close()
      } catch (e) {
        console.warn('Error closing WebSocket during reconnect:', e)
      }
      this.ws = null
    }

    // Clear any pending connection check timeout
    if (this.connectionCheckTimeout) {
      clearTimeout(this.connectionCheckTimeout)
      this.connectionCheckTimeout = null
    }

    setTimeout(() => {
      if (this.shouldReconnect) {
        this.connect()
      }
      // Reset reconnecting flag after connect() completes (it has guards to prevent duplicates)
      // We'll reset it in connect() after it determines if it will actually connect
      setTimeout(() => {
        this.isReconnecting = false
      }, 2000) // Give connect() time to complete
    }, 1000)
  }

  disconnectBc() {
    console.log('[disconnectBc] Setting shouldReconnect = false', {
      roomName: this.roomname,
      docGuid: this.doc?.guid,
      stack: new Error().stack?.split('\n').slice(1, 5).join('\n'),
    })
    this.shouldReconnect = false
    // Stop health monitoring when disconnecting
    this.stopHealthCheck()
    super.disconnectBc()
  }

  destroy() {
    console.log('[destroy] Setting shouldReconnect = false', {
      roomName: this.roomname,
      docGuid: this.doc?.guid,
      stack: new Error().stack?.split('\n').slice(1, 5).join('\n'),
    })
    this.shouldReconnect = false
    // Stop health monitoring when destroying
    this.stopHealthCheck()
    this._stopCreateEvalWaitLoop()
    super.destroy()
  }

}

export { CustomWebsocketProvider }
